import { Suspense, lazy, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";

import { useProject } from "./hooks/useProject";
import { useLeadForm } from "./hooks/useLeadForm";
import { useScrollToHash } from "./hooks/useScrollToHash";
import { DEFAULT_PHONE } from "./utils/constants";

import SEOHelmet from "./components/SEOHelmet";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import AboutSection from "./components/AboutSection";
import LocationSection from "./components/LocationSection";
import ApartmentSection from "./components/ApartmentSection";
import AmenitiesSection from "./components/AmenitiesSection";
import ContactIcons from "./components/ContactIcons";
// Eagerly imported (not lazy) on purpose — see PrivacyPolicyModal.jsx for
// why. It must be ready to open as soon as the app boots, independent of
// Footer's own (lazy-loaded) chunk finishing its network request.
import PrivacyPolicyModal from "./components/PrivacyPolicyModal";

import "./styles/global.css";

// ── Code-split everything below the fold ──────────────────────────────────
// These sections are not visible on first paint, so there's no reason to
// ship their JS in the main bundle. This shrinks the initial download and
// speeds up first paint / time-to-interactive, especially on mobile.
const GallerySection    = lazy(() => import("./components/GallerySection"));
const TowerSection      = lazy(() => import("./components/TowerSection"));
const DeveloperSection  = lazy(() => import("./components/DeveloperSection"));
const ComparisonSection = lazy(() => import("./components/ComparisonSection"));
const LoanSection       = lazy(() => import("./components/LoanSection"));
const FAQSection        = lazy(() => import("./components/FAQSection"));
const SiteVisitSection  = lazy(() => import("./components/SiteVisitSection"));
const ContactUsSection  = lazy(() => import("./components/ContactUsSection"));
const Footer            = lazy(() => import("./components/Footer"));
const LeadPopup         = lazy(() => import("./components/LeadPopup"));

// Applies the per-project brand colors (admin "Theme" tab) as CSS custom
// properties on :root, so every component that already styles itself with
// var(--primary) / var(--accent) picks the project's own colors instead of
// the shared default palette. Falls back to the defaults in global.css
// whenever a project hasn't set a color yet.
// "#0c3b6e" -> "12, 59, 110", used for rgba() shadows/hover tints that
// can't reference a hex custom property directly.
function hexToRgbString(hex) {
  const m = hex?.replace("#", "").match(/.{1,2}/g);
  if (!m || m.length < 3) return null;
  return m.slice(0, 3).map((h) => parseInt(h, 16)).join(", ");
}

function useProjectTheme(theme) {
  useEffect(() => {
    const root = document.documentElement;

    if (theme?.primaryColor) {
      root.style.setProperty("--primary", theme.primaryColor);
      root.style.setProperty("--primary-light", theme.primaryColor);
      const rgb = hexToRgbString(theme.primaryColor);
      if (rgb) root.style.setProperty("--primary-rgb", rgb);
    }
    if (theme?.secondaryColor) {
      root.style.setProperty("--accent", theme.secondaryColor);
      const rgb = hexToRgbString(theme.secondaryColor);
      if (rgb) root.style.setProperty("--accent-rgb", rgb);
    }

    // Mobile browser chrome (Android status/address bar, iOS Safari tab
    // bar) reads a static <meta name="theme-color"> in public/index.html,
    // which was hardcoded and never actually updated to match the
    // project's brand color. Sync it here so it follows --primary too.
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const previousThemeColor = themeColorMeta?.getAttribute("content");
    if (themeColorMeta && theme?.primaryColor) {
      themeColorMeta.setAttribute("content", theme.primaryColor);
    }

    return () => {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--primary-light");
      root.style.removeProperty("--primary-rgb");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--accent-rgb");
      if (themeColorMeta && previousThemeColor !== null) {
        themeColorMeta.setAttribute("content", previousThemeColor);
      }
    };
  }, [theme?.primaryColor, theme?.secondaryColor]);
}

function LoadingSpinner() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <p>Loading project…</p>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <p style={{ color: "red" }}>{message}</p>
    </div>
  );
}

export default function App() {
  const { data, loans, loading, error } = useProject();

  // Scrolls to any DB-named CTA (Tower/Apartment buttons, etc.) or static
  // section whose id matches the current URL hash. Safe to call before
  // `data` exists — it just keeps watching until a matching element shows
  // up, or the hash changes, or it gives up after 15s.
  useScrollToHash();

  const projectId = data?.projectId;

  const leadForm = useLeadForm({
    projectId,
    formDefaults: data?.formDefaults,
    floorOfferText: data?.header?.floorOfferText,
  });

  const handleCta = (event) => {
    leadForm.openPopup(event || "PopUp");
  };

  // Applies the project's brand colors as CSS vars as soon as they're
  // available; safe to call before `data` exists since it's just undefined
  // until then (the hook itself always runs, satisfying the Rules of Hooks).
  useProjectTheme(data?.theme);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorScreen message={error} />;
  if (!data) return null;

  const {
    seo,
    header,
    about,
    location,
    apartment,
    amenities,
    gallery,
    tower,
    comparison,
    developers,
    faq,
    footer,
  } = data;

  // Contact phone number — pulled from the database if the project has set
  // one, otherwise falls back to the shared default. Resolved once here and
  // passed down so every "Call us" link on the page (navbar, footer,
  // floating icons, SEO schema) always stays in sync.
  const phone =
    data?.phone || header?.phone || footer?.phone || DEFAULT_PHONE;

  // Project location, used both to prefill the CRM `location` field and to
  // compose the interaction_description below.
  const projectLocation = data?.formDefaults?.location || "";

  // ✅ Builds the Site Visit interaction payload once the visitor has
  // submitted the inline Name/Phone/Email/Date form in SiteVisitSection,
  // then hands off to the shared OTP flow (see hooks/useLeadForm.js
  // startSiteVisitFlow). On failure, leadForm.otpError is set and
  // SiteVisitSection (still visible, since it's inline now) shows it via
  // its `error` prop.
  const handleSiteVisitSubmit = async ({ fullName, phone: visitorPhone, email, visitDate, visitSlot }) => {
    const projectLabel = header?.projectName || "the project";
    // ✅ visitSlot is one of DateTimeSlotPicker's fixed windows, e.g.
    // "04 PM - 06 PM". The CRM's interaction time field wants a single
    // clock time, not a range, so only the END half goes there — the
    // full range still appears in subject/description for a human reading
    // the lead.
    const slotEndTime = visitSlot.split("-")[1].trim();

    await leadForm.startSiteVisitFlow({
      fullName,
      phone: visitorPhone,
      email,
      interaction: {
        type: "Site Visit",
        date: visitDate,
        time: slotEndTime,
        subject: `Site Visit Request (${visitSlot})`,
        // Composed here (rather than left to the backend's fallback) so the
        // CRM lead's interaction_description always spells out the project,
        // location, requested date/slot AND the visitor's email in one place.
        description: `Site visit requested via website for ${projectLabel} (${
          projectLocation || "location not set"
        }). Preferred date: ${visitDate}, slot: ${visitSlot}.${
          email ? ` Contact email: ${email}.` : ""
        }`,
        dealType: "Buy",
        category: "Residential",
      },
    });
  };

  return (
    <HelmetProvider>

      {/* ✅ SEO + GTM header tracking — all consolidated here, tracking prop passed in */}
      <SEOHelmet
        seo={seo}
        projectId={projectId}
        tracking={data?.tracking}
        phone={phone}
      />

      {/* ✅ GTM noscript body tag */}
      {data?.tracking?.bodyScript && (
        <div dangerouslySetInnerHTML={{ __html: data.tracking.bodyScript }} />
      )}

      {/* Renders null until #PrivacyPolicy is in the URL, so it's harmless
          to mount this early — doing so means it's ready the instant the
          app boots, regardless of how long below-the-fold chunks
          (Footer, etc.) take to arrive on a slow connection. */}
      <PrivacyPolicyModal />

      <Navbar
        projectId={projectId}
        projectName={header?.projectName}
        authorizedChannelPartner={header?.authorizedChannelPartner}
        onCtaClick={handleCta}
        phone={phone}
      />
      <Hero projectId={projectId} header={header} developers={developers} onCtaClick={handleCta} />
      <AboutSection projectId={projectId} about={about} />
      <LocationSection location={location} />

      <ApartmentSection projectId={projectId} apartment={apartment} onCtaClick={handleCta} />
      <AmenitiesSection projectId={projectId} amenities={amenities} />

      {/* Below-the-fold: each wrapped individually so one section loading
          doesn't hold up the others */}
      <Suspense fallback={null}>
        <GallerySection projectId={projectId} gallery={gallery} />
      </Suspense>
      <Suspense fallback={null}>
        <TowerSection projectId={projectId} tower={tower} onCtaClick={handleCta} />
      </Suspense>
      <Suspense fallback={null}>
        <DeveloperSection developers={developers} />
      </Suspense>
      <Suspense fallback={null}>
        <FAQSection faq={faq} />
      </Suspense>
      <Suspense fallback={null}>
        <ComparisonSection comparison={comparison} projectName={header?.projectName} />
      </Suspense>

      {/* Static "Book a Site Visit" (#sitevisit) + "Contact Us" (#contactus)
          — shown side by side on desktop (Site Visit left, Contact Us
          right) and stacked on mobile (Site Visit first, then Contact Us —
          this is just their natural DOM order below). Placed after the
          Comparison section so visitors see it once they've been convinced
          by the comparison data. */}
      <div className="sitevisit-contact-grid">
        <Suspense fallback={null}>
          <SiteVisitSection
            projectName={header?.projectName}
            onSubmit={handleSiteVisitSubmit}
            loading={leadForm.loading}
            error={leadForm.otpError}
          />
        </Suspense>
        <Suspense fallback={null}>
          <ContactUsSection
            phone={phone}
            projectName={header?.projectName}
            onCtaClick={handleCta}
          />
        </Suspense>
      </div>

      <Suspense fallback={null}>
        <LoanSection loans={loans} projectName={header?.projectName} />
      </Suspense>


      <Suspense fallback={null}>
        <Footer projectId={projectId} footer={footer} phone={phone} />
      </Suspense>

      <ContactIcons
        onCtaClick={handleCta}
        projectName={header?.projectName}
        phone={phone}
      />

      <Suspense fallback={null}>
        <LeadPopup leadForm={leadForm} />
      </Suspense>

    </HelmetProvider>
  );
}
